import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { takeChosenBeforeSignIn, useLanguage, type Lang } from '../i18n/i18n';
import { FEATURE_DEFAULTS, type FeatureId } from './features';

export type RoundTransitionType = 'NONE' | 'FADE' | 'SLIDE';

export type Palette =
  | 'theme'
  | 'warm'
  | 'cool'
  | 'stone'
  | 'lavender'
  | 'dark'
  | 'midnight'
  | 'graphite'
  | 'indigo'
  | 'meadow'
  | 'peach'
  | 'sunrise'
  | 'sky'
  | 'ocean'
  | 'paper'
  | 'chalk'
  | 'wood'
  | 'parchment'
  | 'garden'
  | 'storybook'
  | 'toybox'
  | 'glacier'
  | 'blush'
  | 'steel'
  | 'high-contrast';
export type Design =
  | 'classic'
  | 'scholar'
  | 'fresh'
  | 'pastel'
  | 'symbol'
  | 'classroom'
  | 'tactile'
  | 'clinical'
  | 'editorial'
  | 'bold'
  | 'night'
  | 'flow'
  | 'kinetic'
  | 'spring'
  | 'playroom'
  | 'expedition'
  | 'garden'
  | 'popup'
  | 'blocks'
  | 'glass'
  | 'silk'
  | 'chrome';

export const MOVING_DESIGNS: readonly Design[] = ['flow', 'kinetic', 'spring'];

export const DESIGN_PALETTE: Record<Design, Exclude<Palette, 'theme'>> = {
  classic: 'warm',
  scholar: 'indigo',
  fresh: 'meadow',
  pastel: 'peach',
  symbol: 'sunrise',
  classroom: 'lavender',
  tactile: 'sky',
  clinical: 'ocean',
  editorial: 'paper',
  bold: 'chalk',
  night: 'midnight',
  flow: 'sky',
  kinetic: 'ocean',
  spring: 'meadow',
  playroom: 'wood',
  expedition: 'parchment',
  garden: 'garden',
  popup: 'storybook',
  blocks: 'toybox',
  glass: 'glacier',
  silk: 'blush',
  chrome: 'steel',
};
export type TextScale = 'regular' | 'large' | 'larger';
export type FeedbackStrength = 'subtle' | 'regular' | 'strong';
export type BorderWeight = 'regular' | 'strong';
export type LineSpacing = 'regular' | 'relaxed' | 'loose';
export type TileSize = 'regular' | 'large';

export type RewardIcon = 'star' | 'heart' | 'trophy' | 'gem' | 'smiley' | 'leaf' | 'rocket' | 'sun';
export type RewardWhen = 'COMPLETION' | 'EXIT' | 'APPROVAL';
export type RewardCompletionMode = 'SESSION' | 'EVERY_N' | 'MASTERY';

/** What the reward is called and when it is given. A program may override any of it. */
export type RewardSettings = {
  enabled: boolean;
  name: string;
  icon: RewardIcon;
  amount: number;
  when: RewardWhen;
  completionMode: RewardCompletionMode;
  everyN: number;
  /** Widen the gap between rewards as the learner succeeds. */
  thinning: boolean;
  thinStep: number;
  thinMax: number;
};

export const REWARD_ICONS: Record<RewardIcon, string> = {
  star: '⭐',
  heart: '❤️',
  trophy: '🏆',
  gem: '💎',
  smiley: '😊',
  leaf: '🍃',
  rocket: '🚀',
  sun: '☀️',
};

export type UserSettings = {
  /**
   * Replaces themeMode and highContrast, which contradicted each other.
   * Sensory preference varies enormously between autistic people, so this is a
   * genuine setting rather than a decision to make on everyone's behalf.
   */
  palette: Palette;
  design: Design;
  gameDesign: Design;
  language: Lang;
  textScale: TextScale;
  feedbackStrength: FeedbackStrength;
  borderWeight: BorderWeight;
  lineSpacing: LineSpacing;
  tileSize: TileSize;
  /**
   * Teaching defaults differ from the sensory settings above in one way that
   * matters: a program may override them. This is what a program gets when it
   * has no opinion, so changing it moves every program that never disagreed.
   */
  defaultErrorless: 'off' | 'on';
  /**
   * Defaults per kind of game, keyed by kind then by parameter.
   *
   * WHAT REPLACED THREE SETTINGS THAT WERE NEVER GLOBAL. defaultTileCount,
   * defaultUniqueWordCount and defaultErrorless are all properties of a tile
   * match, and were offered as though every game had them — meaningless for 43
   * of the hundred concepts, which have no tiles at all.
   *
   * SPARSE, AND ONLY WHAT WAS CHOSEN. A kind absent here has never been
   * configured and falls back to what the kind itself declares. Which
   * parameters a kind has lives in `concepts/kinds.ts`, so a new kind needs no
   * change to this type, to the settings page, or to the database.
   */
  kindDefaults: Record<string, Record<string, string | number | boolean>>;
  /** @deprecated superseded by `palette`; still sent by older clients */
  themeMode: 'calm-light' | 'calm-dark' | 'high-contrast';
  reduceMotion: boolean;
  ttsVoice: string | null;
  ttsRate: number;
  defaultTileCount: number;
  defaultUniqueWordCount: number;
  highContrast: boolean;
  largeText: boolean;
  promptRepeatSeconds: number;
  correctPingEnabled: boolean;
  roundTransitionMs: number;
  roundTransitionType: RoundTransitionType;
  /** Read-only — true if a supervisor PIN is currently configured. The hash itself never reaches the client. */
  supervisorPinSet: boolean;
  reward: RewardSettings;
  features: Record<FeatureId, boolean>;
};

export const PROMPT_REPEAT_MIN_SECONDS = 5;
export const ROUND_TRANSITION_MIN_MS = 0;
export const ROUND_TRANSITION_MAX_MS = 3000;

const DEFAULTS: UserSettings = {
  palette: 'theme',
  design: 'scholar',
  gameDesign: 'scholar',
  language: 'en',
  textScale: 'regular',
  feedbackStrength: 'regular',
  borderWeight: 'regular',
  lineSpacing: 'regular',
  tileSize: 'regular',
  defaultErrorless: 'off',
  kindDefaults: {},
  themeMode: 'calm-light',
  reduceMotion: true,
  ttsVoice: null,
  ttsRate: 0.9,
  defaultTileCount: 4,
  defaultUniqueWordCount: 4,
  highContrast: false,
  largeText: false,
  promptRepeatSeconds: PROMPT_REPEAT_MIN_SECONDS,
  correctPingEnabled: true,
  roundTransitionMs: 800,
  roundTransitionType: 'FADE',
  supervisorPinSet: false,
  reward: {
    enabled: false,
    name: 'stars',
    icon: 'star',
    amount: 1,
    when: 'COMPLETION',
    completionMode: 'SESSION',
    everyN: 10,
    thinning: false,
    thinStep: 2,
    thinMax: 30,
  },
  features: { ...FEATURE_DEFAULTS },
};

type Api = {
  settings: UserSettings;
  loading: boolean;
  loaded: boolean;
  refresh: () => Promise<void>;
  update: (patch: Partial<UserSettings>) => Promise<void>;
  /** Apply a settings response from a non-/me/settings endpoint (e.g. supervisor-PIN ops). */
  applyServerSettings: (s: Partial<UserSettings>) => void;
};

const LOOK_KEY = 'easylearning.look';
type Look = Pick<UserSettings, 'design' | 'gameDesign' | 'palette' | 'textScale' | 'reduceMotion'>;

function rememberedLook(): Partial<Look> {
  try {
    const raw = localStorage.getItem(LOOK_KEY);
    return raw ? (JSON.parse(raw) as Partial<Look>) : {};
  } catch {
    return {};
  }
}

function rememberLook(s: UserSettings) {
  try {
    const look: Look = { design: s.design, gameDesign: s.gameDesign, palette: s.palette, textScale: s.textScale, reduceMotion: s.reduceMotion };
    localStorage.setItem(LOOK_KEY, JSON.stringify(look));
  } catch {
    return;
  }
}

const Ctx = createContext<Api | null>(null);

export function SettingsProvider({ children }: { children: ReactNode }) {
  const { token } = useAuth();
  const { lang, setLang } = useLanguage();
  const [settings, setSettings] = useState<UserSettings>(() => ({ ...DEFAULTS, ...rememberedLook() }));
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(async () => {
    if (!token) {
      setSettings({ ...DEFAULTS, ...rememberedLook() });
      setLoaded(false);
      return;
    }
    setLoading(true);
    try {
      const s = await api<UserSettings>('/me/settings', { token });
      if (takeChosenBeforeSignIn() || (!s.language || s.language === 'en') && lang !== 'en') {
        if (s.language !== lang) void api('/me/settings', { method: 'PUT', token, body: { language: lang } });
      } else if (s.language && s.language !== lang) {
        setLang(s.language);
      }
      setSettings({ ...DEFAULTS, ...s });
      rememberLook({ ...DEFAULTS, ...s });
      setLoaded(true);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { refresh(); }, [refresh]);

  /*
   * Apply preferences as data attributes the global CSS keys off.
   *
   * ONE PALETTE ATTRIBUTE, NOT A THEME PLUS AN OVERRIDE. This used to read
   * `highContrast ? 'high-contrast' : themeMode`, so a boolean silently won
   * over the dropdown above it — choosing the dark theme with the box ticked
   * gave high contrast with nothing on screen explaining why. Two controls
   * answering one question is exactly the unpredictability this audience is
   * least able to absorb.
   */
  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute('data-text-scale', settings.textScale);
    root.setAttribute('data-feedback', settings.feedbackStrength);
    root.setAttribute('data-border-weight', settings.borderWeight);
    root.setAttribute('data-line-spacing', settings.lineSpacing);
    root.setAttribute('data-tile-size', settings.tileSize);
    root.setAttribute('data-reduce-motion', settings.reduceMotion ? 'true' : 'false');
  }, [settings]);

  const update = useCallback(async (patch: Partial<UserSettings>) => {
    if (!token) return;
    const next = await api<Partial<UserSettings>>('/me/settings', {
      method: 'PUT',
      token,
      body: patch,
    });
    /* Merge order matters: DEFAULTS (baseline) → previous state → patch we just sent →
       server response (authoritative). This means a field we tried to update sticks
       even if the backend response omits it (e.g. running an older API version against
       a newer UI), instead of silently reverting to the default.

       kindDefaults IS MERGED A LEVEL DEEPER, because a shallow spread would let a
       patch carrying one kind replace the whole map and take every other kind's
       settings with it — a supervisor saving a tile-match change would silently
       lose their timed-hold defaults, and nothing on screen would say so. */
    setSettings((prev) => {
      const merged = { ...DEFAULTS, ...prev, ...patch, ...next };
      merged.kindDefaults = {
        ...(prev.kindDefaults ?? {}),
        ...(patch.kindDefaults ?? {}),
        ...(next.kindDefaults ?? {}),
      };
      /* Same reason as kindDefaults: a patch carrying one reward field would otherwise
         replace the whole block and take the name and icon with it. */
      merged.reward = {
        ...DEFAULTS.reward,
        ...(prev.reward ?? {}),
        ...(patch.reward ?? {}),
        ...(next.reward ?? {}),
      };
      merged.features = {
        ...DEFAULTS.features,
        ...(prev.features ?? {}),
        ...(patch.features ?? {}),
        ...(next.features ?? {}),
      };
      rememberLook(merged);
      return merged;
    });
  }, [token]);

  const applyServerSettings = useCallback((s: Partial<UserSettings>) => {
    setSettings((prev) => ({ ...DEFAULTS, ...prev, ...s }));
  }, []);

  const value = useMemo<Api>(() => ({ settings, loading, loaded, refresh, update, applyServerSettings }),
    [settings, loading, loaded, refresh, update, applyServerSettings]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useSettings(): Api {
  const v = useContext(Ctx);
  if (!v) throw new Error('useSettings outside SettingsProvider');
  return v;
}
